import React from "react";

import { screenBreakpoints } from "@busbud/horizon";

import URIExpand from "@app/lib/uri-expand";

type BreakpointKeys = keyof typeof screenBreakpoints;

export interface ImageDimension {
  // in pixels
  width: number;
  height: number;
}

export type ImageWidths = { [K in BreakpointKeys]?: number };

interface CommonImgixLazyImageProps {
  // template url with width set a {{width}} and height as {{height}}
  template_url: string;
  alt: string;
  className?: string;
  quality?: number;
}

type ImgixLazySizeBasedImageProps = CommonImgixLazyImageProps & {
  default_size: ImageDimension;
  widths: ImageWidths;
  // multiplicator for pixel densities ,eg. 2, 3, 2.6 etc.
  densities?: number[];
};

type ImgixLazyDensityBasedImageProps = CommonImgixLazyImageProps & {
  densities: number[];
};

type ImgixLazyImageProps =
  | CommonImgixLazyImageProps
  | ImgixLazySizeBasedImageProps
  | ImgixLazyDensityBasedImageProps;

export const ImgixLazyImage: React.FC<ImgixLazyImageProps> = props => {
  const { className, alt, template_url, quality = 100 } = props;

  if (!template_url.includes("imgix")) {
    return (
      <img loading="lazy" className={className} alt={alt} src={template_url} />
    );
  }

  const sizes =
    "widths" in props
      ? computeSizes(props.widths, props.default_size)
      : undefined;

  const srcSet =
    "widths" in props
      ? computeSizeBasedSrcSet(
          template_url,
          props.default_size,
          Object.values(props.widths),
          props.densities,
          quality
        )
      : "densities" in props && props.densities
        ? computePixelDensityBasedSrcSet(template_url, props.densities)
        : undefined;

  const src =
    "default_size" in props
      ? URIExpand(template_url, {
          width: props.default_size.width.toString(),
          height: props.default_size.height.toString(),
          q: quality.toString()
        }).toString()
      : template_url;

  return (
    <img
      loading="lazy"
      className={className}
      alt={alt}
      src={src}
      srcSet={srcSet}
      sizes={sizes}
    />
  );
};

function computePixelDensityBasedSrcSet(
  template_url: string,
  densities: number[]
) {
  return densities
    .map(dpr => getPixelDensitySrcSetEntry(template_url, dpr))
    .join(",");
}

function computeSizeBasedSrcSet(
  template_url: string,
  default_size: ImageDimension,
  widths: number[],
  densities: number[] | undefined,
  quality: number
): string {
  const all_srcsets = widths.flatMap(value => {
    const dprs = [...new Set([...(densities ?? []), 1])];

    return dprs.map(dpr =>
      getSizeBasedSrcSetEntry(template_url, default_size, value * dpr, quality)
    );
  });
  const uniq_srcsets = [...new Set(all_srcsets)];

  return uniq_srcsets.join(",");
}

function getSizeBasedSrcSetEntry(
  template_url: string,
  default_size: ImageDimension,
  width: number,
  quality: number
) {
  const src = URIExpand(template_url, {
    width: width.toString(),
    height: Math.floor(
      (default_size.height * width) / default_size.width
    ).toString(),
    q: quality.toString()
  }).toString();

  return `${src} ${width}w`;
}

function getPixelDensitySrcSetEntry(template_url: string, density: number) {
  const src = URIExpand(template_url, {})
    .setQuery({ dpr: `${density}` })
    .toString();

  return `${src} ${density}x`;
}

const breakpoint_keys_order = ["xl", "lg", "md", "sm", "xs"] as const;

function computeSizes(widths: ImageWidths, default_size: ImageDimension) {
  const breakpoint_sorted_by_screensize = (
    Object.entries(widths) as [BreakpointKeys, number][]
  ).sort(
    (a, b) =>
      breakpoint_keys_order.indexOf(a[0]) - breakpoint_keys_order.indexOf(b[0])
  );
  if (breakpoint_sorted_by_screensize.length === 0) {
    return undefined;
  }

  const breakpoint_width_pairs = breakpoint_sorted_by_screensize.reduce<
    [BreakpointKeys, number][]
  >((acc, [key, number], idx) => {
    if (acc[idx - 1]?.[1] === number) {
      acc[idx - 1] = [key, number];
    } else {
      acc.push([key, number]);
    }

    return acc;
  }, []);

  const breakpoint_sizes = breakpoint_width_pairs.map(([key, value]) => {
    // we're mobile first, so the media query for `xs` is `(min-width: 0)`
    // So, we're saving a couple bytes by not using the media query at all
    if (key === "xs") {
      return `${value}px`;
    }
    return `(min-width:${screenBreakpoints[key]}) ${value}px`;
  });

  const breakpoint_sizes_with_default = !widths?.xs
    ? [...breakpoint_sizes, `${default_size.width}px`]
    : breakpoint_sizes;

  return breakpoint_sizes_with_default?.join(",");
}
